import { ThemeComponent } from '@/theme/ThemeComponent';
import { OptionSelector } from '@/utils/OptionSelectors';
import { Component, Prop, Ref, SimpleEvent } from '@eastsideco/salvo-ts';
import { ProductOption } from '@/components/Product/ProductOptionPicker';

@Component
export class ProductDropdownOption extends ThemeComponent implements ProductOption {
    @Prop name!: string;
    @Prop values!: string[];
    @Prop selected!: string|null;
    @Prop position!: number;
    @Prop emptyOption!: boolean;
    @Prop({default: false}) isFrameset!: boolean;

    @Ref input!: HTMLSelectElement;

    private valuesWithoutStock: string[] = [];
    private valuesWithoutVariant: string[] = [];

    private _onChange = new SimpleEvent<string|null>();
    public get onChange() {
        return this._onChange.asEvent();
    }

    init() {
        this.setupAccessibility();
        this.$listen(this.input, 'change', (e) => {
            this.valueChanged();
        });
        this.$listen(this.input, 'keydown', (e) => {
            this.handleKeyDown(e);
        });
        this.valueChanged();
    }

    private setupAccessibility() {
        // Set proper ARIA attributes
        this.input.setAttribute('aria-label', `${this.name} selection`);
        this.input.setAttribute('aria-describedby', `option-description-${this.position}`);

        // Add role and other accessibility attributes
        this.input.setAttribute('role', 'combobox');
        this.input.setAttribute('aria-expanded', 'false');

        // Create description element for screen readers
        const description = document.createElement('div');
        description.id = `option-description-${this.position}`;
        description.className = 'sr-only';
        description.textContent =
            `Select ${this.name.toLowerCase()} option. Use arrow keys to navigate and Enter to select.`;
        this.appendChild(description);
    }

    private handleKeyDown(event: KeyboardEvent) {
        switch (event.key) {
            case 'Enter':
                // Prevent form submission on Enter
                event.preventDefault();
                this.input.focus();
                break;
            case 'Escape':
                // Close dropdown and return focus
                this.input.blur();
                break;
            case 'Tab':
                // Allow normal tab behavior
                break;
            default:
                // Allow other keys for normal select behavior
                break;
        }
    }

    private valueChanged() {
        this.selected = this.input.value || null;
        this._onChange.dispatch(this.selected);

        // Update ARIA attributes
        this.updateAriaAttributes();
    }

    private updateAriaAttributes() {
        const selectedOption = this.input.options[this.input.selectedIndex];
        if (selectedOption) {
            this.input.setAttribute('aria-activedescendant', selectedOption.value);

            // Update description for screen readers
            const description = this.querySelector(`#option-description-${this.position}`) as HTMLElement;
            if (description) {
                const availability = this.getAvailabilityText(selectedOption.value);
                description.textContent = `${this.name} selected: ${selectedOption.text}${availability}`;
            }
        }
    }

    private getAvailabilityText(value: string): string {
        if (this.valuesWithoutStock.includes(value)) {
            return ' - Out of stock';
        } else if (this.valuesWithoutVariant.includes(value)) {
            return ' - Not available';
        }
        return '';
    }

    public select(value: string|null) {
        this.selected = this.input.value;
        this.input.value = value || '';
        this.updateAriaAttributes();
    }

    public updateBasedOnSelector(selector: OptionSelector) {
        this.valuesWithoutStock = selector.options[this.position - 1].values
            .filter((v) => v.outOfStock).map((v) => v.value);
        this.valuesWithoutVariant = selector.options[this.position - 1].values
            .filter((v) => !v.variantExists).map((v) => v.value);

        this.updateOptionLabels();
        this.updateAriaAttributes();
    }

    public updateOptionLabels() {
        for (const optionEl of this.input.options) {
            optionEl.label = optionEl.value;
            if (this.valuesWithoutStock.indexOf(optionEl.value) > -1){
                optionEl.label += ' - Out of stock';
            } else if (this.valuesWithoutVariant.indexOf(optionEl.value) > -1) {
                optionEl.label += ' - Not available';
            }
        }
    }
}
